import { FlatTreeControl } from '@angular/cdk/tree';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { makeNewFolderFactory, makeTransientFolder } from '@shared/models/folders/helpers';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { ExpandableOptions, ExpandableResource } from '../tree-data-table/expandable-resource';
import { getParentNode, treeNodesReplacer } from './helpers';

const getLevel = (node: ExpandableResource<any>) => node.level;
const isExpandable = (node: ExpandableResource<any>) => node.isContainer;
const getChildren = (node: ExpandableResource<any>): ExpandableResource<any>[] => node.children || [];

export class EmptyFolderName implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@UntilDestroy()
@Component({
  selector: 'miq-create-select-folders',
  templateUrl: './create-select-folders.component.html',
  styleUrls: ['./create-select-folders.component.scss'],
  standalone: false,
})
export class CreateSelectFoldersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() nodes: ExpandableResource<any>[];
  @Input() expandableOptions: ExpandableOptions;
  @Input() selectionModeOnly = false;
  @Input() nodeDataParser = { displayFn: ctx => ctx };
  @Input() enableNone = false;

  @Output() itemCreated = new EventEmitter<any>();
  @Output() itemSelected = new EventEmitter<any>();

  @ViewChild('inputFolderNameRef', { static: true })
  private inputFolderNameRef: MatFormField;

  treeControl: FlatTreeControl<ExpandableResource<any>>;
  dataSource: MatTreeFlatDataSource<ExpandableResource<any>, ExpandableResource<any>>;
  folderFormControl = new UntypedFormControl('', []);
  noNameMatcher = new EmptyFolderName();

  private treeFlattener: MatTreeFlattener<ExpandableResource<any>, ExpandableResource<any>>;
  private nestedNodeMap = new Map<ExpandableResource<any>, ExpandableResource<any>>();
  private dataChange$ = new BehaviorSubject<ExpandableResource<any>[]>([]);
  /**
   * The expanded elements are being resseted when datasource is updated, so adding this
   * to keep the reference o them and recover after datasource is updated
   * */
  private expandedElements = [];

  @Input() hasNoContent = () => false;

  constructor() {
    this.treeFlattener = new MatTreeFlattener(this.transformer.bind(this), getLevel, isExpandable, getChildren);
    this.treeControl = new FlatTreeControl<ExpandableResource<any>>(getLevel, isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngOnChanges({ nodes }: SimpleChanges) {
    if (nodes && nodes.currentValue) {
      this.updateNodes();
    }
  }

  ngOnInit() {
    this.dataChange$.pipe(untilDestroyed(this)).subscribe(nodes => {
      this.dataSource.data = nodes;
      this.treeControl.dataNodes
        .filter((folder: any) => this.expandedElements.some(({ id: expandedId }) => expandedId === folder.id))
        .forEach((node: any) => {
          node.expanded = true;
          this.treeControl.expand(node);
        });
    });
  }

  ngOnDestroy() {
    /** Required by untilDestroy */
    this.inputFolderNameRef = null;
  }

  transformer = (node: ExpandableResource<any>, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode === node ? existingNode : new ExpandableResource(node, this.expandableOptions);
    flatNode.level = level;
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  updateNodes() {
    const createOrSelectNone = this.selectionModeOnly ? makeTransientFolder() : makeNewFolderFactory();
    return this.dataChange$.next([createOrSelectNone, ...this.nodes]);
  }

  toggleNode(node) {
    node.toggle();
    this.expandedElements = node.expanded
      ? [...this.expandedElements, node]
      : this.expandedElements.filter(folder => folder.id !== node.id);
  }

  addNewItem(node: any) {
    const newFolder = makeNewFolderFactory();
    const level = this.treeControl.getLevel(node);
    newFolder.level = level;
    if (!node.children || node.children.every(({ id }) => !!id)) {
      node.insertChild(newFolder);
      const currentDs = this.dataChange$.value;
      this.dataChange$.next(treeNodesReplacer(currentDs, node));
    }
  }

  /** Builds the name and makes the call to create, we add | any type to avoid TS lint errors
   * due to ExpandableResource missing attributes it does not recognize since a ExpandableResource is agnostic to
   * other fields than expandable, id, expanded
   */
  saveNode(node: ExpandableResource<any> | any) {
    const parentNode = getParentNode(this.treeControl, node);
    this.folderFormControl.setValidators([Validators.required, Validators.minLength(1)]);
    const value = this.folderFormControl.value.trim();

    if (!value) {
      this.folderFormControl.markAsDirty();
      this.folderFormControl.setErrors([{ empty: true }]);
      this.folderFormControl.setValue(value);
      return false;
    }
    node.name = `${parentNode ? `${parentNode.name}/` : ''}${encodeURIComponent(value)}`;
    this.itemCreated.emit({ node, parentNode, value });
    this.resetFormControl();
  }

  updateSelection(item: ExpandableResource<any>) {
    // selected does not exist as base attribute in ExpandableResource however, is added here on the fly
    // due specific functionality that occurs only in this component. (can be added if used in any other place)
    this.treeControl.dataNodes.forEach((folder: any) => (folder.selected = folder.id === item.id));
  }

  clearIfEmpty() {
    const value = this.folderFormControl.value.trim();
    if (!value) {
      this.resetFormControl();
    }
  }

  resetFormControl() {
    this.folderFormControl.markAsPristine();
    this.folderFormControl.setValue('');
    this.folderFormControl.setErrors([]);
    this.folderFormControl.markAsUntouched();
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.clickedOutside(event)) {
      this.resetFormControl();
    }
  }

  private clickedOutside(event) {
    return this.inputFolderNameRef && !this.inputFolderNameRef._elementRef.nativeElement.contains(event.target);
  }
}
